import React, { useEffect, useState } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { XMarkIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { getStripeForAccount, createPackagePaymentIntent, postPackagePaymentSave, formatCents } from '../services/paymentService';

interface PackagePaymentModalProps {
  packagePaymentId: string;
  packageName: string;
  label: string;
  onClose: () => void;
  onSuccess: () => void;
}

const CheckoutForm: React.FC<{
  packagePaymentId: string;
  clientSecret: string;
  customerId: string;
  amountCents: number;
  currency: string;
  existingPaymentMethodId: string | null;
  cardLast4: string | null;
  onSuccess: () => void;
  onClose: () => void;
}> = ({ packagePaymentId, clientSecret, customerId, amountCents, currency, existingPaymentMethodId, cardLast4, onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useDifferentCard, setUseDifferentCard] = useState(false);
  const useSavedCard = !!(existingPaymentMethodId && cardLast4 && !useDifferentCard);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !clientSecret) return;
    setLoading(true);
    setError(null);

    let confirmResult;
    if (useSavedCard && existingPaymentMethodId) {
      confirmResult = await stripe.confirmCardPayment(clientSecret, { payment_method: existingPaymentMethodId });
    } else {
      if (!elements) return;
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) return;
      confirmResult = await stripe.confirmCardPayment(clientSecret, { payment_method: { card: cardElement } });
    }

    const { error: stripeError, paymentIntent } = confirmResult;
    if (stripeError) {
      setError(stripeError.message || 'Payment failed');
      setLoading(false);
    } else if (paymentIntent?.status === 'succeeded') {
      const pmId = paymentIntent.payment_method
        ? (typeof paymentIntent.payment_method === 'string' ? paymentIntent.payment_method : (paymentIntent.payment_method as any).id)
        : null;
      if (pmId && customerId) {
        await postPackagePaymentSave(packagePaymentId, pmId, customerId, paymentIntent.id).catch(() => {});
      }
      onSuccess();
    } else {
      setError('Unexpected payment status. Please try again.');
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
        <span className="text-sm text-gray-600">Package payment</span>
        <span className="text-lg font-semibold text-gray-900">{formatCents(amountCents, currency)}</span>
      </div>

      {existingPaymentMethodId && cardLast4 ? (
        <div className="space-y-3">
          {!useDifferentCard ? (
            <div className="p-3 border border-gray-300 rounded-lg bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <LockClosedIcon className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">Use saved card ending in <strong>{cardLast4}</strong></span>
              </div>
              <button type="button" onClick={() => setUseDifferentCard(true)} className="text-xs text-primary-600 hover:underline">
                Use a different card
              </button>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New card details</label>
                <div className="p-3 border border-gray-300 rounded-lg">
                  <CardElement options={{ style: { base: { fontSize: '15px', color: '#374151', '::placeholder': { color: '#9CA3AF' } }, invalid: { color: '#EF4444' } } }} />
                </div>
              </div>
              <button type="button" onClick={() => setUseDifferentCard(false)} className="text-xs text-primary-600 hover:underline">
                Use saved card ending in {cardLast4}
              </button>
            </>
          )}
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Card details</label>
          <div className="p-3 border border-gray-300 rounded-lg">
            <CardElement options={{ style: { base: { fontSize: '15px', color: '#374151', '::placeholder': { color: '#9CA3AF' } }, invalid: { color: '#EF4444' } } }} />
          </div>
        </div>
      )}

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium rounded-lg px-4 py-3 transition-colors"
      >
        <LockClosedIcon className="w-4 h-4" />
        {loading ? 'Processing…' : useSavedCard ? `Pay ${formatCents(amountCents, currency)} with saved card` : `Pay ${formatCents(amountCents, currency)}`}
      </button>

      <p className="text-xs text-center text-gray-400 flex items-center justify-center gap-1">
        <LockClosedIcon className="w-3 h-3" />
        Secured by Stripe · Your card info is never stored on our servers
      </p>
    </form>
  );
};

const PackagePaymentModal: React.FC<PackagePaymentModalProps> = ({ packagePaymentId, packageName, label, onClose, onSuccess }) => {
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeInstance, setStripeInstance] = useState<any>(null);
  const [succeeded, setSucceeded] = useState(false);
  const [intentData, setIntentData] = useState<{
    clientSecret: string;
    customerId: string;
    amountCents: number;
    currency: string;
    existingPaymentMethodId: string | null;
    cardLast4: string | null;
  } | null>(null);
  const [intentError, setIntentError] = useState<string | null>(null);

  useEffect(() => {
    createPackagePaymentIntent(packagePaymentId)
      .then((result) => {
        setIntentData({
          clientSecret: result.client_secret,
          customerId: result.customer_id,
          amountCents: result.amount_cents,
          currency: result.currency,
          existingPaymentMethodId: result.existing_payment_method_id ?? null,
          cardLast4: result.card_last4 ?? null,
        });
        return getStripeForAccount(result.stripe_account_id);
      })
      .then((s) => { setStripeInstance(s); setStripeReady(true); })
      .catch((e) => { setIntentError(e.message || 'Failed to initialise payment'); setStripeReady(true); });
  }, [packagePaymentId]);

  function handleSuccess() {
    setSucceeded(true);
    setTimeout(() => { onSuccess(); onClose(); }, 1800);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Package Payment</h2>
            <p className="text-sm text-gray-500 mt-0.5 truncate max-w-xs">{packageName} — {label}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {succeeded ? (
            <div className="py-8 text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-lg font-semibold text-gray-900">Payment successful!</p>
              <p className="text-sm text-gray-500 mt-1">Your package payment has been processed.</p>
            </div>
          ) : !stripeReady ? (
            <div className="py-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : intentError ? (
            <div className="py-6 text-center">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">{intentError}</div>
              <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
            </div>
          ) : !stripeInstance || !intentData ? (
            <div className="py-6 text-center">
              <p className="text-sm text-gray-500">Payment processing is not available right now.</p>
              <button onClick={onClose} className="mt-3 text-sm text-primary-600 hover:underline">Close</button>
            </div>
          ) : (
            <Elements stripe={stripeInstance}>
              <CheckoutForm
                packagePaymentId={packagePaymentId}
                clientSecret={intentData.clientSecret}
                customerId={intentData.customerId}
                amountCents={intentData.amountCents}
                currency={intentData.currency}
                existingPaymentMethodId={intentData.existingPaymentMethodId}
                cardLast4={intentData.cardLast4}
                onSuccess={handleSuccess}
                onClose={onClose}
              />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackagePaymentModal;
